import { useTranslation } from 'react-i18next';
import { AccountStatusCounts, AccountWorkflowStatus, ReportedAccountStatus, withStyles } from 'vopay';
import type { PartnerAccountStatusFilter } from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import { usePartnerData } from '@/providers/PartnerData.provider';
import ProgramTermsCard from './ProgramTermsCard';
import AccountStatusSummaryCard from './AccountStatusSummaryCard';
import getProgramTermsVariant from './programTermsVariant';
import styles from './AccountsOverview.module.scss';

const cn = withStyles(styles);

type AccountsOverviewProps = {
  /** True until the accounts listing (which carries the terms) has responded. */
  fetchingProgramTerms: boolean;
  revShare: string | null;
  minimumProgramFee: string | null;
  /** True until the status summary has responded (the list fetches it, and shares it with its filter). */
  fetchingStatusSummary: boolean;
  numTotalAccounts: number;
  statusCounts: Partial<AccountStatusCounts>;
  /** The stage tiles, in order; the same list the Status filter offers. */
  stages: AccountWorkflowStatus[];
  /** The status the list is filtered to; the matching stage tile renders selected. */
  selectedStatus: PartnerAccountStatusFilter;
  /** A tile was clicked; the list filters to it, or clears the filter when it was the selected one. */
  onStatusClick: (status: ReportedAccountStatus) => void;
};

export default function AccountsOverview({
  fetchingProgramTerms,
  revShare,
  minimumProgramFee,
  fetchingStatusSummary,
  numTotalAccounts,
  statusCounts,
  stages,
  selectedStatus,
  onStatusClick,
}: AccountsOverviewProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });

  const {
    response: { partnerTypeLabel },
  } = usePartnerData();

  const programTermsVariant = getProgramTermsVariant(partnerTypeLabel);

  return (
    <div className={cn('overview')} aria-label={t('accounts', { defaultValue: 'Accounts' })}>
      {programTermsVariant && (
        <ProgramTermsCard
          variant={programTermsVariant}
          fetching={fetchingProgramTerms}
          revShare={revShare}
          minimumProgramFee={minimumProgramFee}
        />
      )}

      <AccountStatusSummaryCard
        fetching={fetchingStatusSummary}
        numTotalAccounts={numTotalAccounts}
        statusCounts={statusCounts}
        stages={stages}
        selectedStatus={selectedStatus}
        onStatusClick={onStatusClick}
      />
    </div>
  );
}
